import { UpdatePasswordRequest } from '@sic-lib/types/Password';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { EditUserTwilioPhoneData } from '../../types/EditUserTwilioPhoneData';
import { EditUserData, NewUserData } from '../../types/NewEditUser';
import {
    requestCreateUser,
    requestEditUser,
    requestEditUsers,
    requestUpdatePassword,
    requestUpdateUser,
    requestUpdateUsersPhoneSettings,
    requestUsersPhoneSettings,
} from '../users/users';

export const useCreateUser = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data: NewUserData) => (await requestCreateUser(data)).data,
        onSuccess: async data => {
            queryClient.invalidateQueries({ queryKey: ['editUser'] });
        },
    });
};

export const useUpdateUser = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data: EditUserData) => (await requestUpdateUser(data)).data,
        onSuccess: async data => {
            queryClient.invalidateQueries({ queryKey: ['editUser'] });
            queryClient.invalidateQueries({ queryKey: ['userConfig'] });
        },
    });
};

export const useEditUsers = () =>
    useQuery({
        queryKey: ['editUsers'],
        queryFn: async () => (await requestEditUsers()).data,
    });

export const useEditUser = (userName: string | undefined) =>
    useQuery({
        enabled: userName != null,
        queryKey: ['editUser', userName],
        queryFn: async () => (await requestEditUser(userName)).data,
    });

export const useUserPhoneSettings = () => {
    return useQuery({
        queryKey: ['editUserPhoneSettings'],
        queryFn: async () => (await requestUsersPhoneSettings()).data,
    });
};

export const useUserUpdatePhoneSettings = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data: EditUserTwilioPhoneData) =>
            (await requestUpdateUsersPhoneSettings(data)).data,
        onSuccess: async data => {
            queryClient.invalidateQueries({ queryKey: ['editUserPhoneSettings'] });
        },
    });
};

export const useUserUpdatePassword = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data: UpdatePasswordRequest) => (await requestUpdatePassword(data)).data,
        onSuccess: async data => {
            queryClient.invalidateQueries({ queryKey: ['editUserUpdatePassword'] });
        },
    });
};
